import {
  LDS_AUTH_RUNTIME_PROFILE_SELECTOR,
  readLDSAuthRuntimeProfile,
  replaceLDSAuthFrameOrigin,
  resolveLDSAuthRuntimeOrigin,
} from '@laserpecker/lds-auth-runtime-profile'

const MIN_HEIGHT = 320
const AUTH_FRAME_MAX_HEIGHT = 3000
const ACCOUNT_HEIGHT_OFFSET = 76
const RESIZE_FEEDBACK_TOLERANCE = 32
const RESIZE_FEEDBACK_WINDOW_MS = 100
const FRAME_BOOTSTRAP_KEY = '__ldsAuthFrameBootstrap'
const FRAME_BOOTSTRAP_MESSAGE_LIMIT = 20
const PAGE_LOADING_SELECTOR = '[data-lds-auth-page-loading]'
const PAGE_LOADING_FAIL_OPEN_MS = 10000
const ACCOUNT_CONTACT_CONFIG_RETRY_DELAYS = [250, 1000, 2500]
const SHOPIFY_ACCOUNT_ORIGIN = 'https://shopify-account.laserpecker.net'
const ACCOUNT_TABS = new Set(['order', 'address', 'seeds', 'training'])
const ACCOUNT_TRACKING_POSITIONS = new Map([
  ['order', 'My Order'],
  ['address', 'My Address'],
  ['seeds', 'My Seeds'],
])
const ACCOUNT_MASK_ACTIONS = new Map([
  ['addMask', { target: 'sections', open: true }],
  ['deleteMask', { target: 'sections', open: false }],
  ['LSO_Mask_Open', { target: 'viewport', open: true }],
  ['LSO_Mask_Close', { target: 'viewport', open: false }],
])

function normalizedPathname(pathname) {
  return pathname === '/' ? pathname : pathname.replace(/\/+$/, '')
}

export function resolveSignedInAccountRedirect(responseUrl, currentOrigin, accountUrl) {
  try {
    const current = new URL(currentOrigin)
    const account = new URL(accountUrl, current)
    const response = new URL(responseUrl, current)
    if (account.origin !== current.origin || response.origin !== current.origin) return null
    if (normalizedPathname(response.pathname) !== normalizedPathname(account.pathname)) return null
    return account.pathname
  } catch {
    return null
  }
}

export function resolveAccountTab(search = '') {
  try {
    const value = new URLSearchParams(search).get('tab')?.toLowerCase() ?? ''
    return ACCOUNT_TABS.has(value) ? value : ''
  } catch {
    return ''
  }
}

function withAccountTab(frameSource, search, shopifyAccount) {
  try {
    const source = new URL(frameSource)
    if (shopifyAccount) {
      const tab = new URLSearchParams(search).get('tab') || ''
      const route =
        tab === 'order'
          ? 'orders'
          : tab === 'address'
            ? 'addresses'
            : tab === 'Training'
              ? 'training'
              : tab
      source.pathname = `/${route}`
      return source.toString()
    }
    const queryIndex = source.hash.indexOf('?')
    const accountQuery = queryIndex >= 0 ? source.hash.slice(queryIndex) : ''
    source.hash = `#/account/${resolveAccountTab(search)}${accountQuery}`
    return source.toString()
  } catch {
    return null
  }
}

export function resolveFrameSource(
  productionSource,
  productionOrigin,
  profile,
  mode = 'login',
  search = '',
) {
  const runtimeOrigin = resolveLDSAuthRuntimeOrigin(productionOrigin, profile)
  if (!runtimeOrigin) return null

  const runtimeSource = replaceLDSAuthFrameOrigin(productionSource, productionOrigin, runtimeOrigin)
  if (!runtimeSource) return null
  return mode === 'account'
    ? withAccountTab(runtimeSource, search, productionOrigin === SHOPIFY_ACCOUNT_ORIGIN)
    : runtimeSource
}

function takeFrameBootstrap(host) {
  const bootstrap = host?.[FRAME_BOOTSTRAP_KEY]
  if (!bootstrap || typeof bootstrap !== 'object') return null

  bootstrap.disconnect?.()
  delete host[FRAME_BOOTSTRAP_KEY]
  return {
    loaded: bootstrap.loaded === true,
    expired: bootstrap.expired === true,
    expiresAt: Number.isFinite(bootstrap.expiresAt) ? bootstrap.expiresAt : undefined,
    messages: [
      ...(Array.isArray(bootstrap.messages)
        ? bootstrap.messages.slice(0, FRAME_BOOTSTRAP_MESSAGE_LIMIT)
        : []),
      ...(bootstrap.modalMessage ? [bootstrap.modalMessage] : []),
    ],
  }
}

function validFrameMessageData(event, iframe, allowedOrigin) {
  if (!event || event.origin !== allowedOrigin || event.source !== iframe?.contentWindow)
    return null
  const data = event.data
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const prototype = Object.getPrototypeOf(data)
  if (prototype !== Object.prototype && prototype !== null) return null
  return data
}

function normalizedAccountTab(value) {
  if (typeof value !== 'string') return null
  const tab = value.toLowerCase()
  return ACCOUNT_TABS.has(tab) ? tab : null
}

export function validAccountNavigationTab(event, iframe, allowedOrigin, mode = 'account') {
  if (mode !== 'account') return null
  const data = validFrameMessageData(event, iframe, allowedOrigin)
  if (!data) return null
  const keys = Object.keys(data).sort()
  if (
    keys.length !== 2 ||
    keys[0] !== 'data' ||
    keys[1] !== 'event' ||
    data.event !== 'changUrlParams'
  ) {
    return null
  }
  return normalizedAccountTab(data.data)
}

export function resolveAccountNavigationUrl(currentUrl, tab) {
  const normalizedTab = normalizedAccountTab(tab)
  if (!normalizedTab) return null

  try {
    const url = new URL(currentUrl)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
    url.searchParams.set('tab', normalizedTab)
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

export function accountNavigationTrackingPayload(tab) {
  const position = ACCOUNT_TRACKING_POSITIONS.get(normalizedAccountTab(tab))
  if (!position) return null
  return {
    event: 'lp_click_my_account',
    lp_track: 'click_my_account',
    lp_track_context: 'account_center',
    lp_track_source: 'vendor',
    lp_tracking_owner: 'lds_account_navigation',
    lp_position: position,
  }
}

export function applyAccountNavigationMessage(
  event,
  iframe,
  allowedOrigin,
  { mode = 'account', currentUrl, history, dataLayerHost } = {},
) {
  const tab = validAccountNavigationTab(event, iframe, allowedOrigin, mode)
  const url = tab && resolveAccountNavigationUrl(currentUrl, tab)
  if (!tab || !url || typeof history?.replaceState !== 'function') return null

  try {
    history.replaceState(history.state, '', url)
  } catch {
    return null
  }

  const payload = accountNavigationTrackingPayload(tab)
  if (payload && dataLayerHost) {
    if (dataLayerHost.dataLayer == null) dataLayerHost.dataLayer = []
    if (typeof dataLayerHost.dataLayer?.push === 'function') {
      dataLayerHost.dataLayer.push(payload)
    }
  }

  return { tab, url, payload }
}

export function validResizeHeight(
  event,
  iframe,
  allowedOrigin,
  mode = 'login',
  shopifyAccount = false,
) {
  const data = validFrameMessageData(event, iframe, allowedOrigin)
  if (!data) return null
  if (mode === 'account' && shopifyAccount) {
    return Number.isFinite(data.height) && data.height > 0 ? Math.ceil(data.height) : null
  }
  const keys = Object.keys(data).sort()
  const legacy = keys.length === 1 && keys[0] === 'height'
  const typed =
    keys.length === 2 && keys[0] === 'height' && keys[1] === 'type' && data.type === 'lds:resize'
  if (!legacy && !typed) return null
  if (
    !Number.isFinite(data.height) ||
    data.height < MIN_HEIGHT ||
    (mode !== 'account' && data.height > AUTH_FRAME_MAX_HEIGHT)
  )
    return null
  return Math.round(data.height)
}

export function validAccountMaskAction(event, iframe, allowedOrigin, mode = 'account') {
  if (mode !== 'account') return null
  const data = validFrameMessageData(event, iframe, allowedOrigin)
  if (!data) return null
  const keys = Object.keys(data)
  if (keys.length !== 1 || keys[0] !== 'event' || typeof data.event !== 'string') return null
  return ACCOUNT_MASK_ACTIONS.get(data.event) ?? null
}

export function validAccountContactAction(
  event,
  iframe,
  allowedOrigin,
  mode = 'account',
  config = null,
) {
  if (mode !== 'account') return null
  const data = validFrameMessageData(event, iframe, allowedOrigin)
  if (!data) return null
  const keys = Object.keys(data)
  if (keys.length === 2 && keys.includes('event') && keys.includes('data')) {
    const detail = data.data
    if (
      data.event === 'accountContactClick' &&
      config?.tracking?.store === 'laserpeckerjp' &&
      config?.message?.provider === 'line' &&
      detail &&
      typeof detail === 'object' &&
      Object.keys(detail).sort().join(',') === 'name,position,provider' &&
      detail.provider === config.message.provider &&
      detail.name === config.message.name &&
      detail.position === config.tracking.position
    )
      return 'click'
    return null
  }
  if (keys.length !== 1 || keys[0] !== 'event') return null
  if (data.event === 'accountContactReady') return 'ready'
  if (data.event === 'accountContactClick') return 'click'
  return null
}

function presentString(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

export function resolveAccountContactConfig(dataset = {}) {
  if (dataset.contactEnabled !== 'true') return null
  const provider = presentString(dataset.contactProvider)
  const app = presentString(dataset.contactApp)
  const name = presentString(dataset.contactName)
  const body = presentString(dataset.contactBody)
  const ctaLabel = presentString(dataset.contactCtaLabel)
  const url = presentString(dataset.contactUrl)
  const store = presentString(dataset.contactStore)
  const pageType = presentString(dataset.contactPageType)
  const position = presentString(dataset.contactPosition)
  if (!provider || !app || !name || !body || !ctaLabel || !url || !store || !pageType || !position)
    return null
  if (!['line', 'whatsapp'].includes(provider)) return null

  let parsedUrl
  try {
    parsedUrl = new URL(url)
  } catch {
    return null
  }
  const trustedContactHost =
    (provider === 'whatsapp' && parsedUrl.hostname === 'wa.me') ||
    (provider === 'line' && parsedUrl.hostname === 'lin.ee')
  if (
    parsedUrl.protocol !== 'https:' ||
    parsedUrl.username ||
    parsedUrl.password ||
    !trustedContactHost
  )
    return null

  return {
    message: { enabled: true, provider, name, body, ctaLabel, url: parsedUrl.href },
    tracking: { app, store, pageType, position },
  }
}

export function postAccountContactConfig(iframe, allowedOrigin, config) {
  if (!iframe?.contentWindow || !allowedOrigin || !config?.message) return false
  iframe.contentWindow.postMessage(
    { event: 'accountContactConfig', data: config.message },
    allowedOrigin,
  )
  return true
}

export function scheduleAccountContactConfigRetries(
  iframe,
  allowedOrigin,
  config,
  schedule = globalThis.setTimeout?.bind(globalThis),
) {
  if (
    !iframe?.contentWindow ||
    !allowedOrigin ||
    !config?.message ||
    typeof schedule !== 'function'
  ) {
    return []
  }

  return ACCOUNT_CONTACT_CONFIG_RETRY_DELAYS.map((delay) =>
    schedule(() => postAccountContactConfig(iframe, allowedOrigin, config), delay),
  )
}

export function accountContactTrackingPayload(config) {
  if (!config?.tracking) return null
  return {
    event: 'lp_app_contact_click',
    lp_app: config.tracking.app,
    lp_store: config.tracking.store,
    lp_page_type: config.tracking.pageType,
    lp_position: config.tracking.position,
  }
}

export function pushAccountContactClick(config, host = globalThis.window) {
  const payload = accountContactTrackingPayload(config)
  if (!payload || !host) return null
  host.dataLayer = host.dataLayer || []
  if (typeof host.dataLayer.push !== 'function') return null
  host.dataLayer.push(payload)
  return payload
}

export function resolvedFrameHeight(reportedHeight, viewportHeight, mode = 'login') {
  const viewport = Number.isFinite(viewportHeight) ? Math.round(viewportHeight) : 0
  const viewportFloor = mode === 'register' ? viewport - 140 : viewport
  const legacyReportedHeight =
    Math.round(reportedHeight) + (mode === 'account' ? ACCOUNT_HEIGHT_OFFSET : 0)
  const resolvedHeight = Math.max(640, legacyReportedHeight, viewportFloor)
  return mode === 'account' ? resolvedHeight : Math.min(AUTH_FRAME_MAX_HEIGHT, resolvedHeight)
}

export function shouldApplyResizeHeight(
  reportedHeight,
  currentHeight,
  feedbackState = {},
  now = Date.now(),
) {
  if (!Number.isFinite(currentHeight) || currentHeight <= 0) {
    feedbackState.lastAppliedHeight = undefined
    feedbackState.lastAppliedAt = undefined
    return true
  }

  const upwardDelta = reportedHeight - currentHeight
  if (upwardDelta <= 0 || upwardDelta > RESIZE_FEEDBACK_TOLERANCE) {
    feedbackState.lastAppliedHeight = undefined
    feedbackState.lastAppliedAt = undefined
    return true
  }

  const isImmediateFeedback =
    feedbackState.lastAppliedHeight === currentHeight &&
    Number.isFinite(feedbackState.lastAppliedAt) &&
    now - feedbackState.lastAppliedAt <= RESIZE_FEEDBACK_WINDOW_MS
  if (isImmediateFeedback) return false

  feedbackState.lastAppliedHeight = reportedHeight
  feedbackState.lastAppliedAt = now
  return true
}

if (typeof window !== 'undefined' && typeof HTMLElement !== 'undefined') {
  class LDSAuthAccountAdapter extends HTMLElement {
    openAccountModalMask() {
      if (!this.restoreAccountScroll) {
        const root = document.documentElement
        const body = document.body
        const scrollX = window.scrollX
        const scrollY = window.scrollY
        const scrollbarWidth = Math.max(0, window.innerWidth - root.clientWidth)
        const paddingRight = parseFloat(window.getComputedStyle(body).paddingRight) || 0
        const snapshots = [
          [
            root,
            [
              'overflow-x',
              'overflow-y',
              'overscroll-behavior-x',
              'overscroll-behavior-y',
              'scroll-behavior',
            ],
          ],
          [
            body,
            [
              'position',
              'top',
              'left',
              'width',
              'overflow-x',
              'overflow-y',
              'overscroll-behavior-x',
              'overscroll-behavior-y',
              'padding-right',
            ],
          ],
        ].map(([element, properties]) => [
          element,
          properties.map((property) => [
            property,
            element.style.getPropertyValue(property),
            element.style.getPropertyPriority(property),
          ]),
        ])
        this.restoreAccountScroll = () => {
          for (const [element, properties] of snapshots) {
            for (const [property, value, priority] of properties) {
              if (value) element.style.setProperty(property, value, priority)
              else element.style.removeProperty(property)
            }
          }
          root.style.setProperty('scroll-behavior', 'auto', 'important')
          window.scrollTo(scrollX, scrollY)
          const [, value, priority] = snapshots[0][1].find(
            ([property]) => property === 'scroll-behavior',
          )
          if (value) root.style.setProperty('scroll-behavior', value, priority)
          else root.style.removeProperty('scroll-behavior')
        }
        root.style.setProperty('overflow', 'hidden')
        root.style.setProperty('overscroll-behavior', 'none')
        for (const [property, value] of Object.entries({
          position: 'fixed',
          top: `-${scrollY}px`,
          left: `-${scrollX}px`,
          width: '100%',
          overflow: 'hidden',
          'overscroll-behavior': 'none',
        }))
          body.style.setProperty(property, value)
        if (scrollbarWidth > 0)
          body.style.setProperty('padding-right', `${paddingRight + scrollbarWidth}px`)
      }
      if (this.modalMask) {
        this.modalMask.hidden = false
        this.modalMask.setAttribute('aria-hidden', 'false')
      }
      this.classList.add('lds-auth-account-modal-open')
    }

    closeAccountModalMask() {
      this.activeAccountModal = null
      if (this.accountModalFrame !== undefined) window.cancelAnimationFrame(this.accountModalFrame)
      this.accountModalFrame = undefined
      if (this.modalMask) {
        this.modalMask.hidden = true
        this.modalMask.setAttribute('aria-hidden', 'true')
      }
      this.classList.remove('lds-auth-account-modal-open')
      this.restoreAccountScroll?.()
      this.restoreAccountScroll = undefined
    }

    reportAccountModalReady() {
      if (!this.connected || !this.activeAccountModal || !this.allowedOrigin) return
      const rect = this.iframe.getBoundingClientRect()
      const viewport = window.visualViewport
      const viewportTop = viewport?.offsetTop || 0
      const top = Math.max(0, viewportTop - rect.top)
      const bottom = Math.min(
        rect.height,
        viewportTop + (viewport?.height || window.innerHeight) - rect.top,
      )
      const height = bottom - top
      this.iframe.contentWindow?.postMessage(
        {
          source: 'shopify-account',
          type: 'shopify-account:modal-ready',
          modalId: this.activeAccountModal.id,
          ...(Number.isFinite(top) && Number.isFinite(height) && height > 0
            ? { viewport: { top, height } }
            : {}),
        },
        this.allowedOrigin,
      )
    }

    applyAccountModalMessage(event) {
      if (!this.shopifyAccount) return
      const data = validFrameMessageData(event, this.iframe, this.allowedOrigin)
      if (data?.source !== 'shopify-account') return
      if (data.type === 'shopify-account:modal-open' && presentString(data.modalId)) {
        const modal = { id: data.modalId }
        this.activeAccountModal = modal
        this.openAccountModalMask()
        if (this.accountModalFrame !== undefined)
          window.cancelAnimationFrame(this.accountModalFrame)
        this.accountModalFrame = window.requestAnimationFrame(() => {
          if (!this.connected || this.activeAccountModal !== modal) return
          this.accountModalFrame = undefined
          this.reportAccountModalReady()
        })
      } else if (
        data.type === 'shopify-account:modal-close' &&
        this.activeAccountModal &&
        this.activeAccountModal.id === data.modalId
      ) {
        this.closeAccountModalMask()
      }
    }

    connectedCallback() {
      if (this.connected) return
      this.connected = true
      this.iframe = this.querySelector('iframe')
      this.modalMask = this.querySelector('[data-lds-auth-modal-mask]')
      this.statusMask = this.querySelector('.lds-auth-account-mask')
      this.frameLoaded = this.iframe?.dataset.ldsAuthLoaded === 'true'
      this.setPageLoadingHidden = (hidden) => {
        const pageLoading = document.querySelector(PAGE_LOADING_SELECTOR)
        if (pageLoading) pageLoading.hidden = hidden
      }
      this.pageLoadingExpired = false
      this.clearPageLoadingFailOpen = () => {
        if (
          this.pageLoadingFailOpenTimer !== undefined &&
          typeof window.clearTimeout === 'function'
        ) {
          window.clearTimeout(this.pageLoadingFailOpenTimer)
        }
        this.pageLoadingFailOpenTimer = undefined
      }
      this.schedulePageLoadingFailOpen = (expiresAt) => {
        if (this.frameLoaded || this.pageLoadingExpired) {
          this.setPageLoadingHidden(true)
          return
        }
        if (this.pageLoadingFailOpenTimer !== undefined) return

        const delay = Number.isFinite(expiresAt)
          ? Math.min(PAGE_LOADING_FAIL_OPEN_MS, Math.max(0, expiresAt - Date.now()))
          : PAGE_LOADING_FAIL_OPEN_MS
        if (delay === 0 || typeof window.setTimeout !== 'function') {
          this.pageLoadingExpired = true
          this.setPageLoadingHidden(true)
          return
        }
        this.pageLoadingFailOpenTimer = window.setTimeout(() => {
          this.pageLoadingFailOpenTimer = undefined
          this.pageLoadingExpired = true
          this.setPageLoadingHidden(true)
        }, delay)
      }
      this.productionOrigin = this.dataset.productionOrigin
      this.shopifyAccount =
        this.dataset.mode === 'account' && this.productionOrigin === SHOPIFY_ACCOUNT_ORIGIN
      this.accountUrl = this.dataset.accountUrl
      this.contactConfig = resolveAccountContactConfig(this.dataset)
      this.resizeFeedbackState = {}
      if (!this.iframe || !this.productionOrigin || !this.iframe.dataset.src) {
        this.setPageLoadingHidden(true)
        this.connected = false
        return
      }
      this.abortController = new AbortController()
      this.contactConfigRetryTimers = []
      this.clearAccountContactConfigRetries = () => {
        if (typeof window.clearTimeout === 'function') {
          this.contactConfigRetryTimers.forEach((timer) => window.clearTimeout(timer))
        }
        this.contactConfigRetryTimers = []
      }
      this.scheduleAccountContactConfig = () => {
        this.clearAccountContactConfigRetries()
        postAccountContactConfig(this.iframe, this.allowedOrigin, this.contactConfig)
        this.contactConfigRetryTimers = scheduleAccountContactConfigRetries(
          this.iframe,
          this.allowedOrigin,
          this.contactConfig,
          window.setTimeout?.bind(window),
        )
      }
      this.applyAccountMaskAction = (action) => {
        if (action.target === 'viewport' && this.shopifyAccount) {
          if (action.open) this.openAccountModalMask()
          else this.closeAccountModalMask()
          return
        }
        if (action.target === 'sections') {
          document
            .querySelectorAll('#MainContent .shopify-section, #main .shopify-section')
            .forEach((section) => {
              section.style.backgroundColor = action.open ? '#C8C8C8' : '#FAFAFA'
            })
          return
        }
        if (action.target === 'viewport' && this.modalMask) {
          this.modalMask.hidden = !action.open
          this.modalMask.setAttribute('aria-hidden', String(!action.open))
        }
      }
      this.resetAccountMasks = () => {
        if (this.dataset.mode !== 'account') return
        this.applyAccountMaskAction({ target: 'sections', open: false })
        this.applyAccountMaskAction({ target: 'viewport', open: false })
      }
      this.onMessage = (event) => {
        const height = validResizeHeight(
          event,
          this.iframe,
          this.allowedOrigin,
          this.dataset.mode,
          this.shopifyAccount,
        )
        const currentHeight = this.iframe.getBoundingClientRect().height
        if (
          height !== null &&
          (this.shopifyAccount ||
            shouldApplyResizeHeight(height, currentHeight, this.resizeFeedbackState))
        ) {
          this.reportedHeight = height
          this.updateFrameHeight()
        }
        this.applyAccountModalMessage(event)
        const action = validAccountMaskAction(
          event,
          this.iframe,
          this.allowedOrigin,
          this.dataset.mode,
        )
        if (action) this.applyAccountMaskAction(action)
        applyAccountNavigationMessage(event, this.iframe, this.allowedOrigin, {
          mode: this.dataset.mode,
          currentUrl: window.location.href,
          history: window.history,
          dataLayerHost: window,
        })
        const contactAction = validAccountContactAction(
          event,
          this.iframe,
          this.allowedOrigin,
          this.dataset.mode,
          this.contactConfig,
        )
        if (contactAction === 'ready') {
          postAccountContactConfig(this.iframe, this.allowedOrigin, this.contactConfig)
          this.clearAccountContactConfigRetries()
        } else if (contactAction === 'click') {
          pushAccountContactClick(this.contactConfig, window)
        }
      }
      this.updateFrameHeight = () => {
        if (!Number.isFinite(this.reportedHeight)) return
        const height = this.shopifyAccount
          ? this.reportedHeight
          : resolvedFrameHeight(this.reportedHeight, window.innerHeight, this.dataset.mode)
        this.iframe.style.height = `${height}px`
      }
      this.onViewportResize = () => {
        this.updateFrameHeight()
        this.reportAccountModalReady()
      }
      this.onAccountViewportChange = () => this.reportAccountModalReady()
      this.onLoad = () => {
        try {
          if (this.iframe.contentWindow?.location?.href === 'about:blank') return
        } catch {
          // Cross-origin frames cannot expose their current location.
        }
        if (this.shopifyAccount) this.closeAccountModalMask()
        this.clearPageLoadingFailOpen()
        this.frameLoaded = true
        this.pageLoadingExpired = false
        this.iframe.dataset.ldsAuthLoaded = 'true'
        this.classList.add('lds-auth-account-adapter--loaded')
        this.classList.remove('lds-auth-account-adapter--invalid-profile')
        if (this.statusMask) this.statusMask.hidden = true
        this.setPageLoadingHidden(true)
        this.updateFrameHeight()
        this.scheduleAccountContactConfig()
      }
      this.applyFrameSource = (failOpenState = {}) => {
        const profile = readLDSAuthRuntimeProfile(document)
        const source = resolveFrameSource(
          this.iframe.dataset.src,
          this.productionOrigin,
          profile,
          this.dataset.mode,
          window.location.search,
        )
        const runtimeOrigin = resolveLDSAuthRuntimeOrigin(this.productionOrigin, profile)
        if (!source || !runtimeOrigin) {
          this.clearAccountContactConfigRetries()
          this.clearPageLoadingFailOpen()
          this.allowedOrigin = undefined
          this.frameLoaded = false
          this.pageLoadingExpired = false
          delete this.iframe.dataset.ldsAuthLoaded
          this.iframe.removeAttribute('src')
          this.classList.remove('lds-auth-account-adapter--loaded')
          this.classList.add('lds-auth-account-adapter--invalid-profile')
          this.resetAccountMasks()
          this.setPageLoadingHidden(true)
          const mask = this.statusMask
          if (mask) {
            mask.textContent = this.dataset.invalidProfileMessage
            mask.hidden = false
          }
          return
        }

        this.allowedOrigin = new URL(source).origin
        if (this.iframe.getAttribute('src') === source) {
          if (failOpenState.expired === true) this.pageLoadingExpired = true
          this.schedulePageLoadingFailOpen(failOpenState.expiresAt)
          return
        }
        if (this.shopifyAccount) this.closeAccountModalMask()
        this.clearAccountContactConfigRetries()
        this.clearPageLoadingFailOpen()
        this.frameLoaded = false
        this.pageLoadingExpired = false
        delete this.iframe.dataset.ldsAuthLoaded
        this.classList.remove('lds-auth-account-adapter--loaded')
        if (this.statusMask) this.statusMask.hidden = true
        this.setPageLoadingHidden(false)
        if (this.shopifyAccount) {
          this.iframe.setAttribute('allow', 'fullscreen')
          this.iframe.setAttribute('allowfullscreen', '')
        }
        this.iframe.setAttribute('src', source)
        this.schedulePageLoadingFailOpen()
      }
      this.onSectionUnload = (event) => {
        if (event.target?.contains?.(this)) this.disconnect()
      }
      this.onSectionLoad = (event) => {
        const target = event.target
        const profileReloaded =
          target?.matches?.(LDS_AUTH_RUNTIME_PROFILE_SELECTOR) ||
          target?.querySelector?.(LDS_AUTH_RUNTIME_PROFILE_SELECTOR)
        if (profileReloaded) this.applyFrameSource()

        const pageLoadingReloaded =
          target?.matches?.(PAGE_LOADING_SELECTOR) || target?.querySelector?.(PAGE_LOADING_SELECTOR)
        if (pageLoadingReloaded) {
          this.setPageLoadingHidden(
            this.frameLoaded || this.pageLoadingExpired || !this.allowedOrigin,
          )
        }
      }
      this.probeSignedInAccount = async () => {
        const mode = this.dataset.mode
        if (
          (mode === 'login' || mode === 'register') &&
          this.allowedOrigin &&
          this.iframe.getAttribute('src') &&
          this.accountUrl &&
          typeof window.fetch === 'function'
        ) {
          try {
            const response = await window.fetch(this.accountUrl, {
              method: 'HEAD',
              credentials: 'same-origin',
              redirect: 'follow',
              cache: 'no-store',
              signal: this.abortController.signal,
            })
            if (!this.connected) return
            const redirect =
              response.ok &&
              resolveSignedInAccountRedirect(response.url, window.location.origin, this.accountUrl)
            if (redirect) {
              window.location.replace(redirect)
              return
            }
          } catch (error) {
            if (error?.name === 'AbortError') return
          }
        }
      }
      window.addEventListener('message', this.onMessage)
      window.addEventListener('resize', this.onViewportResize)
      if (this.shopifyAccount) {
        window.addEventListener('scroll', this.onAccountViewportChange)
        this.accountVisualViewport = window.visualViewport
        this.accountVisualViewport?.addEventListener('resize', this.onAccountViewportChange)
        this.accountVisualViewport?.addEventListener('scroll', this.onAccountViewportChange)
      }
      this.iframe.addEventListener('load', this.onLoad)
      document.addEventListener('shopify:section:unload', this.onSectionUnload)
      document.addEventListener('shopify:section:load', this.onSectionLoad)
      document.body.classList.add('lds-auth-account-adapter-open')
      if (this.dataset.mode === 'account') {
        document.body.classList.add('lds-auth-account-center-open')
      }
      const frameBootstrap = takeFrameBootstrap(this)
      this.applyFrameSource(frameBootstrap ?? undefined)
      if (
        (frameBootstrap?.loaded || this.frameLoaded) &&
        this.allowedOrigin &&
        this.iframe.getAttribute('src')
      ) {
        this.onLoad()
      }
      frameBootstrap?.messages.forEach((event) => this.onMessage(event))
      this.probeSignedInAccount()
    }

    disconnectedCallback() {
      this.disconnect()
    }

    disconnect() {
      if (!this.connected) return
      window.removeEventListener('message', this.onMessage)
      window.removeEventListener('resize', this.onViewportResize)
      window.removeEventListener('scroll', this.onAccountViewportChange)
      this.accountVisualViewport?.removeEventListener('resize', this.onAccountViewportChange)
      this.accountVisualViewport?.removeEventListener('scroll', this.onAccountViewportChange)
      if (this.shopifyAccount) this.closeAccountModalMask()
      this.accountVisualViewport = undefined
      this.abortController?.abort()
      this.clearAccountContactConfigRetries?.()
      this.clearPageLoadingFailOpen?.()
      this.iframe?.removeEventListener('load', this.onLoad)
      this.iframe?.removeAttribute('src')
      if (this.iframe?.dataset) delete this.iframe.dataset.ldsAuthLoaded
      this.frameLoaded = false
      this.setPageLoadingHidden?.(true)
      this.resetAccountMasks?.()
      document.removeEventListener('shopify:section:unload', this.onSectionUnload)
      document.removeEventListener('shopify:section:load', this.onSectionLoad)
      document.body.classList.remove('lds-auth-account-adapter-open')
      document.body.classList.remove('lds-auth-account-center-open')
      this.classList.remove('lds-auth-account-adapter--loaded')
      this.classList.remove('lds-auth-account-adapter--invalid-profile')
      this.reportedHeight = undefined
      this.resizeFeedbackState = undefined
      this.allowedOrigin = undefined
      this.accountUrl = undefined
      this.contactConfig = undefined
      this.contactConfigRetryTimers = undefined
      this.clearAccountContactConfigRetries = undefined
      this.scheduleAccountContactConfig = undefined
      this.modalMask = undefined
      this.statusMask = undefined
      this.frameLoaded = undefined
      this.setPageLoadingHidden = undefined
      this.pageLoadingExpired = undefined
      this.pageLoadingFailOpenTimer = undefined
      this.clearPageLoadingFailOpen = undefined
      this.schedulePageLoadingFailOpen = undefined
      this.applyAccountMaskAction = undefined
      this.resetAccountMasks = undefined
      this.abortController = undefined
      this.probeSignedInAccount = undefined
      this.connected = false
    }
  }

  if (!customElements.get('lds-auth-account-adapter')) {
    customElements.define('lds-auth-account-adapter', LDSAuthAccountAdapter)
  }
}
